/**
 * FilterPanel — left sidebar with entity type / category / relation type filters.
 */
import { clsx } from 'clsx';
import { useOntologyStore, type EntityTypeFilter } from '@/stores/ontology';
import { ENTITY_TYPE_COLORS, REL_TYPE_COLORS } from './OntologyGraph';

const FALLBACK_ENTITY_TYPES: EntityTypeFilter[] = ['ATTRIBUTE', 'NEED', 'VALUE', 'TRUST', 'PRODUCT', 'OTHER'];

interface Props {
  graph: {
    meta: {
      categories: string[];
      rel_types: string[];
      entity_types?: string[];
    };
  };
}

export function FilterPanel({ graph }: Props) {
  const filters = useOntologyStore((s) => s.filters);
  const setFilters = useOntologyStore((s) => s.setFilters);

  const ENTITY_TYPES: EntityTypeFilter[] =
    graph.meta.entity_types && graph.meta.entity_types.length > 0
      ? (graph.meta.entity_types as EntityTypeFilter[])
      : FALLBACK_ENTITY_TYPES;

  const allEntityTypesActive = filters.entityTypes.length === 0;

  const toggleEntityType = (et: EntityTypeFilter) => {
    if (allEntityTypesActive) {
      // Clicking a type when "show all" is active → filter TO just that type
      setFilters({ entityTypes: [et] });
      return;
    }
    const next = filters.entityTypes.includes(et)
      ? filters.entityTypes.filter((t) => t !== et)
      : [...filters.entityTypes, et];
    // When all types are deselected, reset to "show all" (empty = no filter)
    setFilters({ entityTypes: next });
  };

  const toggleCategory = (cat: string) => {
    const next = filters.categories.includes(cat)
      ? filters.categories.filter((c) => c !== cat)
      : [...filters.categories, cat];
    setFilters({ categories: next });
  };

  const toggleRelType = (rel: string) => {
    const next = filters.relTypes.includes(rel)
      ? filters.relTypes.filter((r) => r !== rel)
      : [...filters.relTypes, rel];
    setFilters({ relTypes: next });
  };

  const allCatsActive = filters.categories.length === 0;
  const allRelsActive = filters.relTypes.length === 0;

  return (
    <div className="w-52 flex-shrink-0 bg-navy-900 border-r border-navy-700 overflow-y-auto flex flex-col">
      <div className="p-3 border-b border-navy-800">
        <p className="text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider mb-2">
          Node Type
        </p>
        <div className="space-y-1">
          {ENTITY_TYPES.map((et) => {
            const color = ENTITY_TYPE_COLORS[et] ?? ENTITY_TYPE_COLORS['OTHER'];
            const active = allEntityTypesActive || filters.entityTypes.includes(et);
            return (
              <button
                key={et}
                onClick={() => toggleEntityType(et)}
                className={clsx(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors',
                  active ? 'text-white bg-navy-700' : 'text-slate-500 hover:bg-navy-800',
                )}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: active ? color : '#334155' }}
                />
                {et.charAt(0) + et.slice(1).toLowerCase()}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-3 border-b border-navy-800">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider">
            Category
          </p>
          {!allCatsActive && (
            <button
              onClick={() => setFilters({ categories: [] })}
              className="text-[9px] text-brand-teal hover:text-white transition-colors"
            >
              All
            </button>
          )}
        </div>
        <div className="space-y-1">
          {graph.meta.categories.map((cat) => {
            const active = allCatsActive || filters.categories.includes(cat);
            return (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={clsx(
                  'w-full text-left px-2 py-1 rounded text-[11px] transition-colors leading-tight',
                  active ? 'text-slate-200 bg-navy-700' : 'text-slate-600 hover:bg-navy-800',
                )}
              >
                {cat.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider">
            Relation Type
          </p>
          {!allRelsActive && (
            <button
              onClick={() => setFilters({ relTypes: [] })}
              className="text-[9px] text-brand-teal hover:text-white transition-colors"
            >
              All
            </button>
          )}
        </div>
        <div className="space-y-1">
          {graph.meta.rel_types.map((rel) => {
            const active = allRelsActive || filters.relTypes.includes(rel);
            const color = REL_TYPE_COLORS[rel] ?? '#94a3b8';
            return (
              <button
                key={rel}
                onClick={() => toggleRelType(rel)}
                className={clsx(
                  'w-full flex items-center gap-2 text-left px-2 py-1 rounded text-[11px] transition-colors',
                  active ? 'text-slate-200 bg-navy-700' : 'text-slate-600 hover:bg-navy-800',
                )}
              >
                <span
                  className="w-2 h-0.5 flex-shrink-0 rounded"
                  style={{ backgroundColor: active ? color : '#334155' }}
                />
                {rel.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
